import axios from 'axios';
import { API_BASE_URL } from './api';

interface ServiceData {
  service_id?: number;
  category_id: number;
  name: string;
  description?: string;
  duration: number;
  price: number;
  discount_price?: number | null;
  image_url?: string | null;
  status: number; // 1 active, 0 inactive
  display_order?: number;
}

interface ServiceImage {
  id?: number;
  service_id: number;
  image_url: string;
  alt_text?: string | null;
  display_order: number;
}

interface Equipment {
  id: number;
  name: string;
  description?: string;
  quantity: number;
  created_at?: string;
  updated_at?: string;
}

interface Requirement {
  id: number;
  description: string;
  is_mandatory: boolean;
  created_at?: string;
  updated_at?: string;
}

interface AddonService {
  id: number;
  name: string;
  description?: string;
  price: number;
  duration: number;
  is_included: boolean;
  price_override?: number | null;
  category?: string;
  created_at?: string;
  updated_at?: string;
}

export const serviceManagementService = {
  // Service CRUD operations
  async listServices(params?: { category_id?: number; status?: number }) {
    const response = await axios.get(`${API_BASE_URL}/services`, { params });
    return response.data;
  },
  async createService(serviceData: ServiceData) {
    const response = await axios.post(`${API_BASE_URL}/services`, serviceData);
    return response.data;
  },

  async updateService(serviceId: number, serviceData: Partial<ServiceData>) {
    console.log('🔍 API: Updating service ID:', serviceId);
    console.log('🔍 API: Service data:', serviceData);
    console.log('🔍 API: Request URL:', `${API_BASE_URL}/services/${serviceId}`);
    
    try {
      const response = await axios.put(`${API_BASE_URL}/services/${serviceId}`, serviceData);
      console.log('✅ API: Update service response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API: Error updating service:', error);
      throw error;
    }
  },

  async updateServiceStatus(serviceId: number, status: number) {
    // convenience wrapper
    return this.updateService(serviceId, { status });
  },

  async getService(serviceId: number) {
    const response = await axios.get(`${API_BASE_URL}/services/${serviceId}`);
    return response.data;
  },

  // Service Images
  // Single image upload to legacy endpoint (kept for compatibility)
  async uploadServiceImage(serviceId: number, imageFile: File) {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await axios.post(`${API_BASE_URL}/services/${serviceId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Multiple images upload using service-management endpoint
  async uploadServiceImages(serviceId: number, imageFiles: File[]) {
    const formData = new FormData();
    imageFiles.forEach((file) => formData.append('images', file));
    const response = await axios.post(
      `${API_BASE_URL}/service-management/${serviceId}/images`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  async getServiceImages(serviceId: number) {
    const response = await axios.get(`${API_BASE_URL}/services/${serviceId}/images`);
    return response.data;
  },

  async updateServiceImage(serviceId: number, imageId: number, imageData: Partial<ServiceImage>) {
    const response = await axios.put(
      `${API_BASE_URL}/services/${serviceId}/images/${imageId}`,
      imageData
    );
    return response.data;
  },

  async deleteServiceImage(serviceId: number, imageId: number) {
    await axios.delete(`${API_BASE_URL}/services/${serviceId}/images/${imageId}`);
  },

  // Equipment and Requirements
  async getEquipmentList() {
    // Backend mounts cleaning equipment at /api/cleaning-equipment
    const response = await axios.get(`${API_BASE_URL}/cleaning-equipment`);
    return response.data;
  },

  async getRequirementsList() {
    try {
      const response = await axios.get(`${API_BASE_URL}/customer-requirements`);
      console.log('✅ Requirements API response:', response.data);
      
      // Handle different response formats
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else {
        console.warn('Unexpected requirements response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching requirements:', error);
      return [];
    }
  },

  // Equipment methods
  async addServiceEquipment(serviceId: number, data: { equipment_id: number; quantity: number }) {
    const response = await axios.put(
      `${API_BASE_URL}/services/${serviceId}/equipment`,
      data
    );
    
    return response.data;
  },

  async updateServiceEquipment(
    serviceId: number, 
    equipment: Array<{ equipment_id: number; quantity: number }>
  ) {
    // Extract just the equipment IDs for the backend
    const equipmentIds = equipment.map(e => e.equipment_id);
    
    // Make sure we're using the correct base URL with /admin prefix
    const baseUrl = API_BASE_URL.endsWith('/admin') 
      ? API_BASE_URL 
      : `${API_BASE_URL}/admin`;
    
    const response = await axios.put(
      `${baseUrl}/services/${serviceId}/equipment`,
      {  equipment },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  },

  // Requirements methods
  async addServiceRequirement(
    serviceId: number, 
    payload: { requirement_id?: number; description?: string; is_mandatory?: boolean }
  ) {
    const response = await axios.put(
      `${API_BASE_URL}/services/${serviceId}/requirements`, 
      payload,
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  },

  async updateServiceRequirements(
    serviceId: number, 
    requirements: Array<{ requirement_id: number; is_mandatory: boolean }>
  ) {
    const response = await axios.put(
      `${API_BASE_URL}/services/${serviceId}/requirements`,
      { requirements }, // send array object directly
    { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  },

  // Addon services methods
    async getAddonServicesList(serviceId?: number) {
    try {
      // First, get all available addon services
      const allAddonsUrl = `${API_BASE_URL}/addon-services`;
      console.log('📡 Fetching all addon services from:', allAddonsUrl);
      
      const allAddonsResponse = await axios.get(allAddonsUrl);
      console.log('✅ All Addon Services API response:', allAddonsResponse.data);
      
      // Extract addons from the nested data.addonServices array
      const allAddons = allAddonsResponse.data?.data?.addonServices || [];
      console.log('📊 Total addons found:', allAddons.length);
      
      // If no serviceId provided, return all addons as not included
      if (!serviceId) {
        return allAddons.map((addon: any) => ({
          addon_id: addon.addon_id,
          addon_service_id: String(addon.addon_id),
          name: addon.name || 'Unnamed Addon',
          // description: addon.description || '',
          base_price: parseFloat(addon.base_price || '0'),
          // category: addon.category || 'general',
          // duration: addon.duration || 0,
          // status: addon.status,
          // is_included: false
        }));
      }
      
      // If serviceId is provided, get the service details which should include its addons
      const serviceUrl = `${API_BASE_URL}/services/${serviceId}`;
      console.log('📡 Fetching service details from:', serviceUrl);
      
      const serviceResponse = await axios.get(serviceUrl);
      console.log('✅ Service details API response:', serviceResponse.data);
      
      // Get the service's addon IDs from the response
      const serviceAddonIds = serviceResponse.data?.data?.addons?.map((a: any) => 
        a.addon_id || a.addon_service_id
      ) || [];
      
      console.log('🔍 Service addon IDs:', serviceAddonIds);
      
      // Map all addons and mark which ones are included for this service
      return allAddons.map((addon: any) => {
        const addonId = addon.addon_id;
        const isIncluded = serviceAddonIds.includes(addonId);
        
        console.log(`🔍 Addon ${addonId} (${addon.name}):`, 
          isIncluded ? '✅ Included' : '❌ Not included');
        
        return {
          addon_id: addonId,
          addon_service_id: String(addonId),
          name: addon.name || 'Unnamed Addon',
          // description: addon.description || '',
          base_price: parseFloat(addon.base_price || '0'),
          category: addon.category || 'general',
          // duration: addon.duration || 0,
          // status: addon.status,
          // is_included: isIncluded
        };
      });
      
    } catch (error: any) {
      console.error('❌ Error in getAddonServicesList:', error);
      if (error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      return [];
    }
  },


  async updateServiceAddonServices(
    serviceId: number,
    addons: Array<{
      addon_service_id: number | string;
      is_included: boolean;
    }>
  ) {
    try {
      console.log('📡 Updating addon services for service:', serviceId);
      console.log('📝 Addons to update:', addons);
      
      // Extract just the addon IDs for included addons
      const addonIds = addons
        .filter(addon => addon.is_included)
        .map(addon => 
          typeof addon.addon_service_id === 'string'
            ? parseInt(addon.addon_service_id, 10)
            : addon.addon_service_id
        );

      console.log('📤 Sending addon IDs:', addonIds);
      
      if (addonIds.length === 0) {
        console.log('ℹ️ No addons to include, skipping update');
        return { status: 'success', message: 'No addons to update' };
      }
      
      // Call the update endpoint with the new addon data
      const updateUrl = `${API_BASE_URL}/services/${serviceId}/addons`;
      console.log('🔄 Updating service addons at:', updateUrl);
      
      const response = await axios.post(updateUrl, { 
        addons: addonIds 
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Update service addons response:', response.data);
      
      // After successful update, refetch the service details to ensure consistency
      const serviceResponse = await axios.get(`${API_BASE_URL}/services/${serviceId}`);
      console.log('🔄 Refreshed service data:', serviceResponse.data);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Error updating addon services:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      throw error;
    }
  },

  // Get service with all related data (fallback to individual API calls)
  async getCompleteService(serviceId: number) {
    console.log('🔍 API: Getting complete service for ID:', serviceId);
    
    try {
      // First try to get basic service data
      console.log('🔍 API: Request URL:', `${API_BASE_URL}/services/${serviceId}`);
      const serviceResponse = await axios.get(`${API_BASE_URL}/services/${serviceId}`);
      console.log('✅ API: Basic service response:', serviceResponse.data);
      
      // Handle the API response format: Direct response with service_id
      let serviceData: any;
      if (serviceResponse.data && serviceResponse.data.service_id) {
        // Direct response format from the new admin API
        serviceData = serviceResponse.data;
      } else if (serviceResponse.data && serviceResponse.data.data && serviceResponse.data.data.service) {
        // Nested response format: { status: "success", data: { service: {...} } }
        serviceData = serviceResponse.data.data.service;
      } else if (serviceResponse.data && serviceResponse.data.service) {
        // Simple nested format: { service: {...} }
        serviceData = serviceResponse.data.service;
      } else {
        console.warn('Unexpected service response format:', serviceResponse.data);
        serviceData = serviceResponse.data;
      }
      
      console.log('🔧 Extracted service data:', serviceData);
      
      // Extract equipment, requirements, and images from the main service response
      let equipment = [];
      let requirements = [];
      let images = [];
      let addons = [];
      
      // The new admin API includes everything in the main response
      if (serviceData.equipment && Array.isArray(serviceData.equipment)) {
        equipment = serviceData.equipment;
        console.log('✅ API: Equipment data from main response:', equipment);
      }
      
      if (serviceData.requirements && Array.isArray(serviceData.requirements)) {
        requirements = serviceData.requirements;
        console.log('✅ API: Requirements data from main response:', requirements);
      }

      if (serviceData.addons && Array.isArray(serviceData.addons)) {
        addons = serviceData.addons;
        console.log('✅ API: Addons data from main response:', addons);
      }
      
      if (serviceData.images && Array.isArray(serviceData.images)) {
        images = serviceData.images;
        console.log('✅ API: Images data from main response:', images);
      }

      
      // Combine all data
      const completeService = {
        ...serviceData,
        equipment,
        requirements,
        addons,
        images
      };
      
      console.log('✅ API: Complete service assembled:', completeService);
      return completeService;
      
    } catch (error) {
      console.error('❌ API: Error getting service:', error);
      throw error;
    }
  },

    //
  // Top Cleaners (Service Providers) APIs
  //

  // Fetch providers flagged as top cleaners
  async getTopCleanerProviders() {
    try {
      const url = `${API_BASE_URL}/staff/top-cleaners`;
      const response = await axios.get(url);
      console.log('📡 Top cleaners API response:', response.data);

      // Expected → response.data.data.staff
      if (response.data?.data?.staff) {
        const staff = response.data.data.staff;
        console.log('✅ Found staff array:', staff);
        // Normalize to expected format
        return staff.map((s: any) => ({
          sp_id: s.sp_id,
          name: s.sp_name,
          photo: s.photo || null
        }));
      }

      // fallback handlers
      if (response.data?.data) return response.data.data;
      if (response.data?.staff) return response.data.staff;
      if (Array.isArray(response.data)) return response.data;

      console.warn("Unexpected format:", response.data);
      return [];
    } catch (error) {
      console.error("Error fetching top cleaners:", error);
      return [];
    }
  },

  // Get top cleaners for a service (list existing rows)
  async getServiceTopCleaners(serviceId: number) {
    const url = `${API_BASE_URL}/services/${serviceId}/top-cleaners`;
    const resp = await axios.get(url);
    // normalize to array
    if (resp.data && resp.data.data && Array.isArray(resp.data.data)) return resp.data.data;
    if (resp.data && Array.isArray(resp.data)) return resp.data;
    return resp.data?.data || [];
  },

  // Add a top cleaner row (multipart FormData)
  async addTopCleaner(serviceId: number, formData: FormData) {
    const url = `${API_BASE_URL}/services/${serviceId}/top-cleaners`;
    const resp = await axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return resp.data;
  },

  // Update an existing top_cleaner row
  async updateTopCleaner(topCleanerId: number, formData: FormData) {
    const url = `${API_BASE_URL}/services/top-cleaners/${topCleanerId}`;
    const resp = await axios.put(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return resp.data;
  },

  // Delete top cleaner row
  async deleteTopCleaner(topCleanerId: number) {
    const url = `${API_BASE_URL}/services/top-cleaners/${topCleanerId}`;
    const resp = await axios.delete(url);
    return resp.data;
  },

};
